/**
 * Dependency Injection Container
 *
 * This module provides DI setup for injecting platform-specific implementations
 * of token storage and observability services.
 */

import 'reflect-metadata';
import { container } from 'tsyringe';

type Lifetime = 'singleton' | 'transient';

// =============================================================================
// SERVICE TOKENS
// =============================================================================

export const TOKENS = {
  TOKEN_STORAGE: Symbol('TokenStorageService'),
  STORAGE: Symbol('StorageService'),
  SYNC_STORAGE: Symbol('SyncStorageService'),
  OBSERVABILITY: Symbol('ObservabilityManager'),
  AUTH_CONFIG: Symbol('AuthConfig'),
  PAYWALL_PRESENTER: Symbol('PaywallPresenter'),
  READ_ONLY_PRESENTER: Symbol('ReadOnlyPresenter'),
  TOKEN_REFRESH_CONFIG: Symbol('TokenRefreshConfig'),
} as const;

// =============================================================================
// DEFAULT SERVICE REGISTRATIONS
// =============================================================================

import { ObservabilityManager } from '../services/observability/observability.manager';
import { NoOpObservabilityProvider } from '../services/observability/providers/no-op.provider';

import type { AuthConfig } from '../types/auth';
import type { ITokenStorageService } from '../types/auth/storage.types';
import type {
  IStorageService,
  ISyncStorageService,
} from '../types/common/storage.types';
import type {
  ObservabilityProvider,
  ObservabilityConfig,
} from '../types/observability';
import type { PaywallPresenter } from '../types/paywall.types';
import type { ReadOnlyPresenter } from '../types/read-only.types';

// Initialize observability with no-op provider by default (safe default)
container.registerInstance<ObservabilityManager>(
  TOKENS.OBSERVABILITY,
  new ObservabilityManager({
    enabled: false,
    provider: new NoOpObservabilityProvider(),
  })
);

// =============================================================================
// SIMPLE DI FUNCTIONS
// =============================================================================

/**
 * Register platform-specific token storage implementation
 */
export const registerTokenStorage = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  implementation: new (...args: any[]) => ITokenStorageService,
  lifetime: Lifetime = 'singleton'
): void => {
  if (lifetime === 'singleton') {
    container.registerSingleton<ITokenStorageService>(
      TOKENS.TOKEN_STORAGE,
      implementation
    );
  } else {
    container.register<ITokenStorageService>(TOKENS.TOKEN_STORAGE, {
      useClass: implementation,
    });
  }
};

/**
 * Get token storage service from container
 */
export const getTokenStorage = (): ITokenStorageService => {
  if (!container.isRegistered(TOKENS.TOKEN_STORAGE)) {
    throw new Error(
      'TokenStorageService has not been registered. Did you call registerTokenStorage()?'
    );
  }
  return container.resolve<ITokenStorageService>(TOKENS.TOKEN_STORAGE);
};

/**
 * Register platform-specific storage implementation
 */
export const registerStorageService = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  implementation: new (...args: any[]) => IStorageService,
  lifetime: Lifetime = 'singleton'
): void => {
  if (lifetime === 'singleton') {
    container.registerSingleton<IStorageService>(
      TOKENS.STORAGE,
      implementation
    );
  } else {
    container.register<IStorageService>(TOKENS.STORAGE, {
      useClass: implementation,
    });
  }
};

/**
 * Get storage service from container
 */
export const getStorageService = (): IStorageService => {
  if (!container.isRegistered(TOKENS.STORAGE)) {
    throw new Error(
      'StorageService has not been registered. Did you call registerStorageService()?'
    );
  }
  return container.resolve<IStorageService>(TOKENS.STORAGE);
};

/**
 * Register a platform-specific SYNCHRONOUS storage implementation (e.g. web's
 * localStorage-backed service). The persist adapter prefers this when present so
 * zustand stores hydrate synchronously; platforms with only async storage
 * (mobile / AsyncStorage) register the async IStorageService instead.
 */
export const registerSyncStorageService = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  implementation: new (...args: any[]) => ISyncStorageService,
  lifetime: Lifetime = 'singleton'
): void => {
  if (lifetime === 'singleton') {
    container.registerSingleton<ISyncStorageService>(
      TOKENS.SYNC_STORAGE,
      implementation
    );
  } else {
    container.register<ISyncStorageService>(TOKENS.SYNC_STORAGE, {
      useClass: implementation,
    });
  }
};

/**
 * Get the synchronous storage service from the container.
 */
export const getSyncStorageService = (): ISyncStorageService => {
  if (!container.isRegistered(TOKENS.SYNC_STORAGE)) {
    throw new Error(
      'SyncStorageService has not been registered. Did you call registerSyncStorageService()?'
    );
  }
  return container.resolve<ISyncStorageService>(TOKENS.SYNC_STORAGE);
};

// =============================================================================
// AUTH CONFIG DI FUNCTIONS
// =============================================================================

/**
 * Register the platform's authentication configuration (mode + transport).
 *
 * Auth mode/transport is a per-platform CONSTANT chosen at the composition root,
 * not runtime state — web registers `session`, mobile registers `token`. Call
 * this once at bootstrap, BEFORE any auth service resolves.
 */
export const registerAuthConfig = (config: AuthConfig): void => {
  container.registerInstance<AuthConfig>(TOKENS.AUTH_CONFIG, config);
};

/**
 * Get the registered auth config. Throws if no platform has registered one —
 * auth mode/transport is a required per-platform constant, so a missing config
 * is a wiring bug (forgot `registerAuthConfig` at the composition root), not
 * something to paper over with a default. Mirrors the storage getters.
 */
export const getAuthConfig = (): AuthConfig => {
  if (!container.isRegistered(TOKENS.AUTH_CONFIG)) {
    throw new Error(
      'AuthConfig has not been registered. Did you call registerAuthConfig() at the platform composition root?'
    );
  }
  return container.resolve<AuthConfig>(TOKENS.AUTH_CONFIG);
};

// =============================================================================
// PAYWALL PRESENTER DI FUNCTIONS
// =============================================================================

/**
 * Register the platform's paywall presenter — the single reaction to a gated
 * request's 402 (docs/prd/prd-feature-gate-refactor.md, D1). Call once at the
 * platform composition root (web: from `PaywallProvider` on mount).
 * Re-registering overwrites, so a remounted provider simply takes over.
 */
export const registerPaywallPresenter = (presenter: PaywallPresenter): void => {
  container.registerInstance<PaywallPresenter>(
    TOKENS.PAYWALL_PRESENTER,
    presenter
  );
};

/**
 * Get the registered paywall presenter, or `undefined` when no platform has
 * registered one. Unlike `getAuthConfig`, absence is NOT a wiring bug: it is
 * the documented safe default (mirroring OBSERVABILITY's no-op) — the paywall
 * publisher treats `undefined` as "present nothing" and the shared package
 * behaves exactly as it did before the seam existed.
 */
export const getPaywallPresenter = (): PaywallPresenter | undefined => {
  if (!container.isRegistered(TOKENS.PAYWALL_PRESENTER)) {
    return undefined;
  }
  return container.resolve<PaywallPresenter>(TOKENS.PAYWALL_PRESENTER);
};

// =============================================================================
// READ-ONLY PRESENTER DI FUNCTIONS
// =============================================================================

/**
 * Register the platform's read-only presenter — the single reaction to a
 * request refused because an admin is signed in as the member. Call once at
 * the platform composition root (web: from `ReadOnlyNotice` on mount).
 * Re-registering overwrites.
 */
export const registerReadOnlyPresenter = (
  presenter: ReadOnlyPresenter
): void => {
  container.registerInstance<ReadOnlyPresenter>(
    TOKENS.READ_ONLY_PRESENTER,
    presenter
  );
};

/**
 * Get the registered read-only presenter, or `undefined` when no platform has
 * registered one — the safe default, as for the paywall presenter.
 */
export const getReadOnlyPresenter = (): ReadOnlyPresenter | undefined => {
  if (!container.isRegistered(TOKENS.READ_ONLY_PRESENTER)) {
    return undefined;
  }
  return container.resolve<ReadOnlyPresenter>(TOKENS.READ_ONLY_PRESENTER);
};

// =============================================================================
// OBSERVABILITY DI FUNCTIONS
// =============================================================================

/**
 * Register observability provider (replaces default no-op provider)
 *
 * Reconfigures the registered manager IN PLACE rather than registering a new
 * one: the retry engine, circuit breaker and deduplication manager each keep
 * the manager they resolved when the request coordinator built them, so a
 * replacement manager would never reach them and they would report to the
 * disabled no-op for the life of the app.
 *
 * @param provider - Platform-specific observability provider (e.g., Sentry, Bugsnag)
 * @param config - Optional observability configuration
 */
export const registerObservability = (
  provider: ObservabilityProvider,
  config?: Partial<ObservabilityConfig>
): void => {
  getObservability().configure({ enabled: true, ...config, provider });
};

/**
 * Get observability manager from container
 *
 * Always returns a valid manager. If no provider has been registered,
 * returns the default manager with no-op provider (safe default).
 */
export const getObservability = (): ObservabilityManager => {
  // Honor the contract above: always return a valid manager. Re-register the
  // default no-op if nothing is registered — e.g. after container.clearInstances()
  // in tests, or before the app wires observability. The API client is now built
  // lazily and may be constructed at any time (including when the container has
  // been cleared); observability must never break that.
  if (!container.isRegistered(TOKENS.OBSERVABILITY)) {
    container.registerInstance<ObservabilityManager>(
      TOKENS.OBSERVABILITY,
      new ObservabilityManager({
        enabled: false,
        provider: new NoOpObservabilityProvider(),
      })
    );
  }
  return container.resolve<ObservabilityManager>(TOKENS.OBSERVABILITY);
};
